// Repo Governance — the header and tabs every governance screen shares.

import * as React from "react";

import { Link } from "@/components/ui/link";
import { usePathname } from "@/lib/navigation";
import { useMe } from "@/lib/query/hooks";
import { cn } from "@/lib/utils";

const TABS = [
  { href: "/governance/security", label: "Security findings" },
  { href: "/governance", label: "Agent config reviews" },
  { href: "/governance/activity", label: "Activity" },
  { href: "/github", label: "Repositories" },
];

/** Org admins change settings; engineers see the same screens read-only. */
export const useIsGovernanceAdmin = () => useMe().data?.role === "tenant_admin";

export function GovernanceHeader({ description }: { description: React.ReactNode }) {
  const pathname = usePathname();
  const isAdmin = useIsGovernanceAdmin();
  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Repo Governance</h1>
        <p className="mt-1 text-sm text-muted-foreground">{description}</p>
        {!isAdmin ? (
          <p className="mt-1 text-xs text-muted-foreground">
            You have read-only access. Org admins change repository settings and triage findings.
          </p>
        ) : null}
      </div>
      <nav className="flex flex-wrap gap-1 border-b border-border" aria-label="Repo Governance">
        {TABS.map((tab) => {
          const active =
            tab.href === "/governance"
              ? pathname === "/governance" || /^\/governance\/\d+/.test(pathname)
              : pathname.startsWith(tab.href);
          return (
            <Link
              key={tab.href}
              href={tab.href}
              aria-current={active ? "page" : undefined}
              className={cn(
                "-mb-px border-b-2 px-3 py-2 text-sm",
                active
                  ? "border-primary font-medium text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground",
              )}
            >
              {tab.label}
            </Link>
          );
        })}
      </nav>
    </div>
  );
}
